import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Package,
  Truck,
  AlertTriangle,
  PackageX,
  TrendingUp,
  ClipboardList,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { dashboardApi, salesApi } from "../lib/api";

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-stone-500">{title}</p>
        <p className="text-2xl font-semibold text-stone-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-2.5 rounded-xl ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const StockItem = ({ product, stock, level }) => {
  const levelConfig = {
    critical: { bg: "bg-rose-50 border-rose-200", text: "text-rose-700", badge: "bg-rose-100 text-rose-700" },
    low: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
    out: { bg: "bg-stone-50 border-stone-200", text: "text-stone-700", badge: "bg-stone-200 text-stone-700" },
  };
  const config = levelConfig[level] || levelConfig.low;

  return (
    <div className={`flex items-center justify-between p-3 rounded-xl border ${config.bg}`}>
      <span className={`text-sm font-medium ${config.text}`}>{product}</span>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.badge}`}>
        {stock <= 0 ? "Habis" : `${stock} pcs`}
      </span>
    </div>
  );
};

const OrderRow = ({ order }) => {
  const statusConfig = {
    pending: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Pending" },
    paid: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Dibayar" },
    processing: { color: "bg-blue-100 text-blue-700", icon: RefreshCw, label: "Proses" },
    shipped: { color: "bg-purple-100 text-purple-700", icon: Truck, label: "Dikirim" },
    cancelled: { color: "bg-rose-100 text-rose-700", icon: XCircle, label: "Batal" },
  };
  const st = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = st.icon;

  return (
    <div className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-stone-900 truncate">{order.invoice || `#${order.id}`}</p>
        <p className="text-xs text-stone-500">{order.customer_name}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
          <StatusIcon className="w-3 h-3" />
          {st.label}
        </span>
      </div>
    </div>
  );
};

const AdminStaffDashboardPage = ({ analyticsToggle }) => {
  const [dashData, setDashData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summary, orders] = await Promise.all([
        dashboardApi.getSummary({ period: "last_7_days" }),
        salesApi.getAll({ limit: 10, page: 1 }).catch(() => ({ sales: [] })),
      ]);
      setDashData(summary);
      setRecentOrders(orders.sales || []);
    } catch (err) {
      toast.error("Gagal memuat data dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
            <p className="mt-2 text-stone-500">Memuat dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const inventory = dashData?.product_intelligence?.inventory_risk || {};
  const lowStock = inventory.low_stock_products || [];
  const outStock = inventory.out_of_stock_products || [];
  const fastSelling = inventory.fast_selling_products || [];

  // Compute stats
  const totalOrders = dashData?.executive_summary?.find((k) => k.key === "total_orders")?.value || 0;
  const productsSold = dashData?.executive_summary?.find((k) => k.key === "products_sold")?.value || 0;
  const pendingShipment = recentOrders.filter(
    (o) => o.payment_status === "paid" && o.shipping_status !== "shipped"
  ).length;
  const criticalStock = lowStock.filter((p) => p.stock <= 5).length + outStock.length;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-staff-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Dashboard Admin</h1>
            <p className="text-stone-500 mt-1">Ringkasan operasional 7 hari terakhir</p>
          </div>
          <div className="flex items-center gap-2">
            {analyticsToggle && (
              <button
                onClick={() => analyticsToggle("analytics")}
                className="inline-flex items-center gap-2 px-4 h-10 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 transition-colors"
              >
                ← Analytics
              </button>
            )}
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 px-4 h-10 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Pesanan Masuk"
            value={totalOrders}
            icon={ClipboardList}
            color="bg-emerald-100 text-emerald-600"
            subtitle="7 hari terakhir"
          />
          <StatCard
            title="Produk Terjual"
            value={productsSold}
            icon={Package}
            color="bg-blue-100 text-blue-600"
            subtitle="7 hari terakhir"
          />
          <StatCard
            title="Perlu Dikirim"
            value={pendingShipment}
            icon={Truck}
            color="bg-purple-100 text-purple-600"
            subtitle="Sudah dibayar"
          />
          <StatCard
            title="Stok Kritis"
            value={criticalStock}
            icon={AlertTriangle}
            color="bg-rose-100 text-rose-600"
            subtitle="Perlu restock"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock alerts */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <PackageX className="w-5 h-5 text-rose-500" />
              Peringatan Stok
            </h2>
            {outStock.length === 0 && lowStock.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Semua stok aman</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {outStock.map((p, i) => (
                  <StockItem key={`out-${i}`} product={p.product} stock={0} level="out" />
                ))}
                {lowStock
                  .sort((a, b) => a.stock - b.stock)
                  .map((p, i) => (
                    <StockItem
                      key={`low-${i}`}
                      product={p.product}
                      stock={p.stock}
                      level={p.stock <= 5 ? "critical" : "low"}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* Recent orders */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5 text-emerald-500" />
              Pesanan Terbaru
            </h2>
            {recentOrders.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Belum ada pesanan</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {recentOrders.slice(0, 8).map((order, i) => (
                  <OrderRow key={order.id || i} order={order} />
                ))}
              </div>
            )}
          </div>

          {/* Fast selling products */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Produk Terlaris
            </h2>
            {fastSelling.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Belum ada data</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {fastSelling.slice(0, 8).map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                    <span className="text-sm text-stone-700 truncate flex-1">{p.product}</span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-stone-900">{p.quantity_sold} pcs</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shipping queue */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-purple-500" />
              Antrian Pengiriman
            </h2>
            {recentOrders.filter((o) => o.payment_status === "paid" && o.shipping_status !== "shipped").length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Tidak ada pesanan menunggu dikirim</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentOrders
                  .filter((o) => o.payment_status === "paid" && o.shipping_status !== "shipped")
                  .map((order, i) => (
                    <div key={order.id || i} className="flex items-center justify-between p-3 rounded-xl border border-purple-100 bg-purple-50">
                      <div>
                        <p className="text-sm font-medium text-purple-900">{order.invoice || `#${order.id}`}</p>
                        <p className="text-xs text-purple-600">{order.customer_name}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        Siap kirim
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminStaffDashboardPage;
